import { TPartnerTypography } from 'partners/types';

/**
 * Waiscen typography — Glass Theme (Figma: Liquid Glass, node 89519-22559).
 *
 * Base font size 16px = 1rem. Line heights are expressed as unitless ratios
 * (design px / font px) so they still scale if the base size ever moves; the
 * Figma px pair is in the comment on every variant.
 *
 * Families resolve through the --font-* variables in ./fonts.ts, per the roles
 * the product team confirmed:
 *   --font-title   Inter           titles
 *   --font-body    Roboto          all other text
 *   --font-caption SF Pro Rounded  warning / info / confirmation messages
 *
 * ⚠ MAPPING GAP: product describes the caption role as covering messages like
 * "this field is required" and "I agree to the terms and policy". Those are
 * rendered with `body2` today (see LoginScreen), so they currently resolve to
 * Roboto, not SF Pro Rounded. Meanwhile label1/label2 — which carry the
 * caption role below, per the Glass token doc — are used for headers such as
 * `skipTrialHeader` and `noSpaceCreated`.
 *
 * The font layer is correct; which variants carry the caption role still needs
 * a decision. Resolving it means either introducing a dedicated helper/message
 * variant, or moving those call sites onto label1/label2.
 */

/** Titles and calls to action. */
const TITLE_FONT = 'var(--font-title)';
/** Running copy. */
const BODY_FONT = 'var(--font-body)';
/** Small labels and captions. */
const CAPTION_FONT = 'var(--font-caption)';

const typography: TPartnerTypography = {
  fontFamily: 'var(--font-base)',
  baselineFontFamily: 'var(--font-baseline)',

  fontSize: 16,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,

  // ------------------------------------------------------------------ titles
  titleLarge: {
    fontFamily: TITLE_FONT,
    fontSize: '1.75rem', // 28px
    fontWeight: 700,
    lineHeight: 34 / 28, // 34px
    letterSpacing: '0'
  },
  titleMedium: {
    fontFamily: TITLE_FONT,
    fontSize: '1.375rem', // 22px
    fontWeight: 600,
    lineHeight: 28 / 22, // 28px
    letterSpacing: '0'
  },
  titleSmall: {
    fontFamily: TITLE_FONT,
    fontSize: '1.125rem', // 18px
    fontWeight: 600,
    lineHeight: 24 / 18, // 24px
    letterSpacing: '0'
  },
  titleExtraSmall: {
    fontFamily: TITLE_FONT,
    fontSize: '1rem', // 16px
    fontWeight: 500,
    lineHeight: 20 / 16, // 20px
    letterSpacing: '0'
  },

  // -------------------------------------------------------------------- body
  bodyLarge: {
    fontFamily: BODY_FONT,
    fontSize: '1rem', // 16px
    fontWeight: 400,
    lineHeight: 24 / 16, // 24px
    letterSpacing: '0.03em'
  },
  bodyMedium: {
    fontFamily: BODY_FONT,
    fontSize: '0.875rem', // 14px
    fontWeight: 400,
    lineHeight: 20 / 14, // 20px
    letterSpacing: '0.02em'
  },
  bodySmall: {
    fontFamily: BODY_FONT,
    fontSize: '0.8125rem', // 13px
    fontWeight: 400,
    lineHeight: 18 / 13, // 18px
    letterSpacing: '0.02em'
  },

  // ------------------------------------------------------------------ labels
  messageLarge: {
    fontFamily: CAPTION_FONT,
    fontSize: '0.75rem', // 12px
    fontWeight: 500,
    lineHeight: 16 / 12, // 16px
    letterSpacing: '0.035em'
  },
  messageSmall: {
    fontFamily: CAPTION_FONT,
    fontSize: '0.6875rem', // 11px
    fontWeight: 500,
    lineHeight: 14 / 11, // 14px
    letterSpacing: '0.035em'
  },
  labelSmall: {
    fontFamily: TITLE_FONT,
    fontSize: '0.625rem', // 10px
    fontWeight: 400,
    lineHeight: 12 / 10, // 12px
    letterSpacing: '0.05em'
  },

  // --------------------------------------------------------------------- CTA
  // The Glass token doc does not specify CTA1/CTA2, but they are live in the
  // app (34 references, 5 as variant="CTA2"), so they cannot simply be
  // dropped. Aligned to the title font at the two CTA sizes already in use.
  // TODO(design): confirm or supply real CTA tokens.
  actionLarge: {
    fontFamily: TITLE_FONT,
    fontSize: '1.125rem', // 18px
    fontWeight: 600,
    lineHeight: 24 / 18, // 24px
    letterSpacing: '0'
  },
  actionMedium: {
    fontFamily: TITLE_FONT,
    fontSize: '1rem', // 16px
    fontWeight: 600,
    lineHeight: 24 / 16, // 24px
    letterSpacing: '0'
  }
};

export default typography;
